import { Button, Card, Form, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ComponentProps, ComponentType } from "react";
import { useEffect, useState } from "react";

import request from "@/api";
import { useUserStore } from "@/stores/react/authStore";
import { Grid as ZhGrid, InputNumber as ZhInputNumber } from "zhihao-ui-react";

const BUSINESS_SETTING_TYPE = 1;

// beta.13 的运行时支持 controls 配置，类型声明未暴露该兼容字段，沿用旧数字输入控件的无按钮模式。
type BusinessInputNumberProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const BusinessInputNumber = ZhInputNumber as ComponentType<BusinessInputNumberProps>;

export interface BusinessSettingContent {
    promoterCommissionSettlementRate?: number;
    promoterCommissionValidityPeriod?: number;
}

export interface BusinessSettingConfig {
    id?: number;
    content?: string;
}

export interface BusinessSettingPageProps {
    /** 可选的测试/嵌入权限覆盖；未传入时沿用当前登录用户权限。 */
    permissions?: string[];
    loadConfig?: (type: number) => Promise<BusinessSettingConfig>;
    saveConfig?: (id: number, params: { type: number; content: string }) => Promise<void>;
}

/** 复用 Vue `/admin/business-config/type?type=1` 接口加载安装推广佣金设置。 */
export async function loadBusinessSettingConfig(type: number): Promise<BusinessSettingConfig> {
    const response = await request.service.get<BusinessSettingConfig>(`/admin/business-config/type?type=${type}`);
    return response.data;
}

/** 复用 Vue 业务设置保存接口，并保持 type/content 的 JSON payload 结构。 */
export async function saveBusinessSettingConfig(id: number, params: { type: number; content: string }): Promise<void> {
    await request.service.put(`/admin/business-config/${id}`, params);
}

function parseBusinessSettingContent(content?: string): BusinessSettingContent {
    if (!content) return {};
    try {
        const parsed = JSON.parse(content) as BusinessSettingContent;
        return {
            promoterCommissionSettlementRate:
                parsed.promoterCommissionSettlementRate == null
                    ? undefined
                    : Number(parsed.promoterCommissionSettlementRate),
            promoterCommissionValidityPeriod:
                parsed.promoterCommissionValidityPeriod == null
                    ? undefined
                    : Number(parsed.promoterCommissionValidityPeriod)
        };
    } catch {
        return {};
    }
}

/** 管理中心业务设置页，保留 Vue 的只读回显、编辑切换、范围校验和保存行为。 */
export function BusinessSettingPage({
    permissions,
    loadConfig = loadBusinessSettingConfig,
    saveConfig = saveBusinessSettingConfig
}: BusinessSettingPageProps) {
    const storeBanButton = useUserStore(state => state.banButton);
    const writeActionsDisabled = permissions ? permissions.includes("ROLE_BUTTON_BAN") : storeBanButton;
    const [values, setValues] = useState<BusinessSettingContent>({});
    const [savedValues, setSavedValues] = useState<BusinessSettingContent>({});
    const [editing, setEditing] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        let disposed = false;
        void loadConfig(BUSINESS_SETTING_TYPE)
            .then(config => {
                if (disposed) return;
                const parsed = parseBusinessSettingContent(config.content);
                setValues(parsed);
                setSavedValues(parsed);
            })
            .catch(() => {
                if (!disposed) message.error("业务设置加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadConfig]);

    const validate = () => {
        const nextErrors: string[] = [];
        const rate = Number(values.promoterCommissionSettlementRate);
        const validityPeriod = Number(values.promoterCommissionValidityPeriod);
        if (!Number.isFinite(rate) || rate < 1 || rate > 99) nextErrors.push("请输入 1-99 之间的比例");
        if (!Number.isFinite(validityPeriod) || validityPeriod < 1 || validityPeriod > 120) {
            nextErrors.push("请输入 1-120 之间的有效期");
        }
        setErrors(nextErrors);
        return nextErrors.length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        try {
            // 沿用 Vue 页面固定更新业务设置主记录 1，避免把回显对象的 id 误当成保存目标。
            await saveConfig(BUSINESS_SETTING_TYPE, {
                type: BUSINESS_SETTING_TYPE,
                content: JSON.stringify(values)
            });
            setSavedValues(values);
            setEditing(false);
            message.success("已保存");
        } catch {
            message.error("业务设置保存失败");
        }
    };

    const handleCancel = () => {
        setValues(savedValues);
        setErrors([]);
        setEditing(false);
    };

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="business-setting-page">
            <Card>
                <Typography.Title level={4}>安装推广佣金设置</Typography.Title>
                {errors.map(error => (
                    <Alert key={error} className="mb-2" message={error} role="alert" type="error" />
                ))}
                <Form layout="vertical">
                    <ZhGrid cols={4} gap={[50, 20]}>
                        <Form.Item label="推介人结佣金额比例">
                            {editing ? (
                                <span className="flex w-full items-center">
                                    <BusinessInputNumber
                                        aria-label="推介人结佣金额比例"
                                        className="w-full"
                                        controls={false}
                                        max={99}
                                        min={1}
                                        onChange={value =>
                                            setValues(current => ({
                                                ...current,
                                                promoterCommissionSettlementRate:
                                                    value === "" ? undefined : Number(value)
                                            }))
                                        }
                                        placeholder="请输入推介人结佣金额比例"
                                        precision={0}
                                        value={values.promoterCommissionSettlementRate ?? ""}
                                    />
                                    <span aria-hidden="true" className="ml-2">
                                        %
                                    </span>
                                </span>
                            ) : (
                                <Typography.Text>
                                    {values.promoterCommissionSettlementRate == null
                                        ? "-"
                                        : `${values.promoterCommissionSettlementRate}%`}
                                </Typography.Text>
                            )}
                        </Form.Item>
                    </ZhGrid>
                    <ZhGrid cols={4} gap={[50, 20]}>
                        <Form.Item label="推介人分佣有效期">
                            {editing ? (
                                <span className="flex w-full items-center">
                                    <BusinessInputNumber
                                        aria-label="推介人分佣有效期"
                                        className="w-full"
                                        controls={false}
                                        max={120}
                                        min={1}
                                        onChange={value =>
                                            setValues(current => ({
                                                ...current,
                                                promoterCommissionValidityPeriod:
                                                    value === "" ? undefined : Number(value)
                                            }))
                                        }
                                        placeholder="请输入推介人分佣有效期"
                                        precision={0}
                                        value={values.promoterCommissionValidityPeriod ?? ""}
                                    />
                                    <span aria-hidden="true" className="ml-2">
                                        月
                                    </span>
                                </span>
                            ) : (
                                <Typography.Text>
                                    {values.promoterCommissionValidityPeriod == null
                                        ? "-"
                                        : `${values.promoterCommissionValidityPeriod}月`}
                                </Typography.Text>
                            )}
                        </Form.Item>
                    </ZhGrid>
                </Form>
                <Space>
                    {editing ? (
                        <>
                            <Button
                                aria-label="保存"
                                disabled={writeActionsDisabled}
                                onClick={() => void handleSave()}
                                type="primary"
                            >
                                保存
                            </Button>
                            <Button aria-label="取消" onClick={handleCancel}>
                                取消
                            </Button>
                        </>
                    ) : (
                        <Button aria-label="修改" disabled={writeActionsDisabled} onClick={() => setEditing(true)}>
                            修改
                        </Button>
                    )}
                </Space>
            </Card>
        </section>
    );
}

export default BusinessSettingPage;
